import { useEffect, useId, useRef, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Модальное подтверждение необратимого действия (CompActions.dc.html).
 *
 * **Фокус по умолчанию — на «Отмена»**: Enter, нажатый по привычке, обязан ничего не
 * удалить. Esc закрывает, Tab ходит только между кнопками окна, а после закрытия фокус
 * возвращается туда, откуда окно открыли, — иначе клавиатурный пользователь оказывается в
 * начале страницы.
 *
 * Только для необратимого. Обратимое действие подтверждения не спрашивает: окно, которое
 * выскакивает на каждый чих, учит нажимать «Да» не читая — и тогда не защищает и здесь.
 */
export interface ConfirmModalProps {
  open: boolean
  title: ReactNode
  /** Что именно пропадёт и что останется. */
  children?: ReactNode
  confirmLabel: string
  cancelLabel?: string
  onConfirm: () => void
  onCancel: () => void
  /** Запрос в пути: кнопка подтверждения неактивна, второе нажатие не уйдёт. */
  pending?: boolean
  icon?: IconName
  /**
   * Weight of the confirming button. Irreversible removal stays `danger`; a reversible bulk
   * action (posting a statement) is `primary` with an accent icon, as in BankRecon.dc.html.
   */
  tone?: 'danger' | 'primary' | 'warning'
  /** Confirming is not possible yet (e.g. a required reason is still empty). */
  confirmDisabled?: boolean
}

export function ConfirmModal({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  pending,
  icon = 'trash',
  tone = 'danger',
  confirmDisabled,
}: ConfirmModalProps) {
  const { t } = useApp()
  const titleId = useId()
  const bodyId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    return () => {
      // Back to where the user was, not to the top of the page.
      opener?.focus?.()
    }
  }, [open])

  if (!open) return null

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      onCancel()
      return
    }
    if (event.key === 'Tab') {
      const buttons = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])',
        ) ?? [],
      )
      if (buttons.length === 0) return
      const first = buttons[0]!
      const last = buttons[buttons.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  return (
    <div className="modal-layer" onKeyDown={onKeyDown}>
      <div
        ref={dialogRef}
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={children ? bodyId : undefined}
      >
        <div className="modal-h">
          <span className={tone === 'danger' ? 'modal-ico' : tone === 'primary' ? 'modal-ico is-accent' : 'modal-ico is-warning'}>
            <Icon name={icon} />
          </span>
          <div className="stack" style={{ gap: 2, paddingTop: 5 }}>
            <span id={titleId} className="h2" style={{ fontSize: 15 }}>
              {title}
            </span>
          </div>
        </div>
        {children && (
          <div className="modal-b" id={bodyId}>
            {children}
          </div>
        )}
        <div className="modal-f">
          <button ref={cancelRef} type="button" className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel ?? t('card.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'danger' ? 'btn btn-danger' : 'btn btn-primary'}
            disabled={pending || confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
